import { App, AuthenticationError, AuthorizationError, ContainerLog, DeploymentLog, NetworkError, Subscription } from '@back4app2/sdk';
import { useEffect, useReducer, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import back4app2 from '../back4app2';
import LoadingSpinner from '../components/LoadingSpinner';
import { BACK4APP_DOT_COM_SITE_URL } from '../settings';
import { AmplitudeEvent, trackEvent } from '../utils/amplitude';
import ContainerLogBox from '../components/ContainerLogBox';
import LogBox from '../components/LogBox';
import { ScreenHead, SegmentedControl, SurfaceCard } from '../components/ds';

interface MyAppLogsState {
  isLoading: boolean;
  loadingErrorMessage?: string;
  logs?: ContainerLog[];
  /* The build log of the last deployment — the other half of the pair the
     overview shows. This screen only ever carried the runtime half, so
     "Open logs" from the overview's Build tab landed somewhere that could not
     show a build. */
  isLoadingDeploymentLogs: boolean;
  deploymentLogsLoadingErrorMessage?: string;
  deploymentLogs?: DeploymentLog[];
  autoScroll: boolean;
}

const INITIAL_STATE: MyAppLogsState = {
  isLoading: true,
  isLoadingDeploymentLogs: true,
  autoScroll: true
};

enum MyAppLogsActionType {
  RESET,
  FINISH_LOADING,
  FINISH_LOADING_DEPLOYMENT_LOGS,
  SET_AUTO_SCROLL
}

const reset = () => ({
  type: MyAppLogsActionType.RESET
} as const);

const finishLoading = (errorMessage?: string, logs?: ContainerLog[]) => ({
  type: MyAppLogsActionType.FINISH_LOADING,
  payload: {
    errorMessage,
    logs
  }
} as const);

const finishLoadingDeploymentLogs = (errorMessage?: string, deploymentLogs?: DeploymentLog[]) => ({
  type: MyAppLogsActionType.FINISH_LOADING_DEPLOYMENT_LOGS,
  payload: {
    errorMessage,
    deploymentLogs
  }
} as const);

const setAutoScroll = (value: boolean) => ({
  type: MyAppLogsActionType.SET_AUTO_SCROLL,
  payload: {
    value
  }
} as const);

type MyAppLogsAction = ReturnType<typeof reset> | ReturnType<typeof finishLoading> | ReturnType<typeof finishLoadingDeploymentLogs> | ReturnType<typeof setAutoScroll>;

const reducer = (state: MyAppLogsState = INITIAL_STATE, action: MyAppLogsAction): MyAppLogsState => {
  switch (action.type) {
    case MyAppLogsActionType.RESET:
      return INITIAL_STATE;
    
    case MyAppLogsActionType.FINISH_LOADING:
      return {
        ...state,
        isLoading: false,
        loadingErrorMessage: action.payload.errorMessage,
        logs: action.payload.logs
      };

    case MyAppLogsActionType.FINISH_LOADING_DEPLOYMENT_LOGS:
      return {
        ...state,
        isLoadingDeploymentLogs: false,
        deploymentLogsLoadingErrorMessage: action.payload.errorMessage,
        deploymentLogs: action.payload.deploymentLogs
      };

    case MyAppLogsActionType.SET_AUTO_SCROLL:
      return {
        ...state,
        autoScroll: action.payload.value
      };
  }
};

const MyAppLogs = () => {
  const { app } = useOutletContext<{ app: App }>();
  const mainServiceEnvironment = app && app.mainService && app.mainService.mainServiceEnvironment;
  const mainServiceEnvironmentId = mainServiceEnvironment && mainServiceEnvironment.id;
  const lastDeployment = mainServiceEnvironment && mainServiceEnvironment.lastDeployment;
  const lastDeploymentId = lastDeployment && lastDeployment.id;
  const [state, dispatch] = useReducer(reducer, INITIAL_STATE);
  const [logTab, setLogTab] = useState<'build' | 'runtime'>('runtime');
  let {
    isLoading, loadingErrorMessage, logs,
    isLoadingDeploymentLogs, deploymentLogsLoadingErrorMessage, deploymentLogs
  } = state;

  useEffect(() => {
    trackEvent(AmplitudeEvent.AT_APP_LOGS_PAGE);
  }, []);

  useEffect(
    () => {
      let subscription: Subscription;

      if (mainServiceEnvironmentId) {
        subscription = back4app2.subscribeToContainersLogs(
          mainServiceEnvironmentId,
          (error, snapshot) => {
            if (error) {
              if (error instanceof NetworkError) {
                console.error('network error', error);
                dispatch(finishLoading('Network error when loading app logs. Check your internet connection and try again.'));
              } else if (error instanceof AuthenticationError || error instanceof AuthorizationError) {
                window.location.replace(`${BACK4APP_DOT_COM_SITE_URL}/login?return-url=${encodeURIComponent(window.location.href)}`);
              } else {
                console.error('unexpected error loading app logs', error);
                dispatch(finishLoading('Unexpected error when loading app logs. Please try again.'));
              }
            } else {
              dispatch(finishLoading(undefined, snapshot));
            }
          }
        );
      }

      return () => {
        if (subscription) {
          subscription.unsubscribe();
          dispatch(reset());
        }
      };
    },
    [
      mainServiceEnvironmentId
    ]
  );

  useEffect(
    () => {
      let subscription: Subscription;

      if (lastDeploymentId) {
        subscription = back4app2.subscribeToDeploymentLogs(
          lastDeploymentId,
          (error, snapshot) => {
            if (error) {
              if (error instanceof NetworkError) {
                console.error('network error', error);
                dispatch(finishLoadingDeploymentLogs('Network error when loading build logs. Check your internet connection and try again.'));
              } else if (error instanceof AuthenticationError || error instanceof AuthorizationError) {
                window.location.replace(`${BACK4APP_DOT_COM_SITE_URL}/login?return-url=${encodeURIComponent(window.location.href)}`);
              } else {
                console.error('unexpected error loading build logs', error);
                dispatch(finishLoadingDeploymentLogs('Unexpected error when loading build logs. Please try again.'));
              }
            } else {
              dispatch(finishLoadingDeploymentLogs(undefined, snapshot));
            }
          }
        );
      }

      return () => {
        if (subscription) {
          subscription.unsubscribe();
        }
      };
    },
    [
      lastDeploymentId
    ]
  );

  return (
    /* min-h-0 lets the log well take the remaining height of the scroll
       container instead of the old hard-coded `100vh - 50px - 12rem`, which
       assumed the desktop navbar and the desktop padding. */
    <div className="flex-1 min-h-0 flex flex-col gap-4">
      <ScreenHead title="Logs" className="flex-none" />

      <SurfaceCard tone="surface" className="flex-1 min-h-[24rem] flex flex-col overflow-hidden">
        {/* The same pair the overview offers. This screen used to show only the
            runtime half, so "Open logs" from the overview's Build tab arrived
            somewhere that could not show a build. */}
        <div className="flex-none px-3.5 py-2.5 border-b border-navy-700 flex items-center gap-3">
          <SegmentedControl
            ariaLabel="Log source"
            value={logTab}
            onChange={setLogTab}
            segments={[
              { value: 'build', label: 'Deployment log' },
              { value: 'runtime', label: 'Application log' },
            ]}
          />
        </div>

        <div className="flex-none px-3.5 py-2 bg-navy-900 border-b border-navy-700 flex items-center justify-between gap-3">
          <span className="font-courrier text-ds-115 text-navy-300 truncate">
            {logTab === 'build'
              ? (lastDeploymentId ? `deployment ${lastDeploymentId.slice(0, 8)}` : 'no deployment yet')
              : 'container output'}
          </span>
          {logTab === 'runtime' && mainServiceEnvironmentId ? (
            <span className="flex items-center gap-1.5 flex-none">
              <span className="w-1.5 h-1.5 rounded-full bg-state-ok-soft" />
              <span className="font-courrier text-ds-105 uppercase tracking-[0.14em] text-state-ok-soft">live</span>
            </span>
          ) : null}
        </div>

        {logTab === 'build' ? (
          lastDeploymentId ? (
            <div className="flex-1 min-h-0">
              <LogBox
                isLoading={isLoadingDeploymentLogs}
                errorMessage={deploymentLogsLoadingErrorMessage}
                logs={deploymentLogs}
                canMaximize
                fullHeight
              />
            </div>
          ) : (
            <div className="flex-1 bg-navy-950 py-10 text-center text-ds-135 text-navy-300">
              Nothing built yet.
            </div>
          )
        ) : isLoading && mainServiceEnvironmentId ? (
          <div className="flex flex-col justify-center items-center flex-1 bg-navy-950">
            <LoadingSpinner />
            <div className="mt-4 text-ds-135 text-navy-200">Loading logs</div>
          </div>
        ) : (
          <div className="flex-1 min-h-0">
            <ContainerLogBox isLoading={isLoading} errorMessage={loadingErrorMessage} logs={logs} canMaximize fullHeight />
          </div>
        )}
      </SurfaceCard>
    </div>
  );
};

export default MyAppLogs;
